import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { Modal } from "@circleco/compass/components/Modal";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { ContactSupport } from "../ContactSupport";
import { Loader } from "../Loader";
import { UpgradeFlow } from "../UpgradeFlow";
import { useUpgradePlanContext } from "../UpgradePlanContext";
import {
  UpgradePlanModalSettersProvider,
  useUpgradePlanModalConfig,
} from "../UpgradePlanModalConfigContext";
import type { UpgradePlanModalComponentProps } from "./UpgradePlanModalWrapper";

const i18nRoot = "pricing_plans.upgrade_modal";

interface UpgradePlanModalCompassContentProps extends UpgradePlanModalComponentProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const UpgradePlanModalCompassContent = ({
  open,
  onOpenChange,
  subheading,
  onSuccess,
  successButtonLabel,
  onCloseModal,
}: UpgradePlanModalCompassContentProps) => {
  const {
    isPlanLoading,
    isEmailHubPlanLoading,
    errorFetchingEmailHubPlan,
    errorFetchingPlan,
    isEmailHubRequired,
    shouldSkipEmailHubReload,
  } = useUpgradePlanContext();

  const { refetch: refetchPundit } = usePunditUserContext();
  const {
    footerConfig,
    headerConfig,
    setFooterConfig,
    setHeaderConfig,
    shouldShowFooter,
  } = useUpgradePlanModalConfig();

  // Bump the flow's key only on the closed -> open edge, so a fresh
  // UpgradeFlow (and its router) mounts each time the modal opens. Compass
  // keeps Modal.Content mounted during its ~300ms close transition, so
  // bumping on every toggle (including closing) remounted UpgradeFlow while
  // the modal was still visibly fading out, briefly flashing whatever screen
  // the freshly recomputed initial path pointed to (e.g. "Cancel downgrade"
  // right after scheduling one).
  const [flowSessionKey, setFlowSessionKey] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setFlowSessionKey(key => key + 1);
    }
  }

  const handleOnSuccess = async () => {
    await refetchPundit();
    onOpenChange(false);
    if (isEmailHubRequired && !shouldSkipEmailHubReload) {
      // [TODO][EmailHubBilling][AfterRelease] Remove this and diagnose the actual issue
      window.location.reload();
      return;
    }
    if (onSuccess) {
      await onSuccess();
    }
  };

  const handleOnClose = async () => {
    await refetchPundit();
    if (onCloseModal) {
      await onCloseModal();
    }
    onOpenChange(false);
  };

  return (
    <Modal.Root
      open={open}
      onOpenChange={open => {
        if (!open) {
          void handleOnClose();
        }
      }}
      size="md"
    >
      <Modal.Content>
        {headerConfig && <Modal.Header title={headerConfig.title} />}

        <Modal.Body>
          <UpgradePlanModalSettersProvider
            setFooterConfig={setFooterConfig}
            setHeaderConfig={setHeaderConfig}
          >
            {isPlanLoading || isEmailHubPlanLoading ? (
              <Loader title={t([i18nRoot, "loading.plan"])} />
            ) : errorFetchingPlan || errorFetchingEmailHubPlan ? (
              <ContactSupport
                title={t([i18nRoot, "errors.plan_not_found.title"])}
                errorMessage={t([i18nRoot, "errors.plan_not_found.message"])}
              />
            ) : (
              <UpgradeFlow
                key={flowSessionKey}
                subheading={subheading}
                successButtonLabel={successButtonLabel}
                handleOnSuccess={handleOnSuccess}
                modal={{ hide: () => onOpenChange(false) }}
              />
            )}
          </UpgradePlanModalSettersProvider>
        </Modal.Body>
        {footerConfig?.primaryAction && (
          <Modal.Footer
            className={footerConfig.className}
            layout={footerConfig.layout}
            primaryAction={footerConfig.primaryAction}
            secondaryAction={footerConfig.secondaryAction}
          />
        )}
        {shouldShowFooter && footerConfig && !footerConfig.primaryAction && (
          <footer
            className={classNames(
              "flex items-center justify-end gap-x-3 px-6 py-4",
              {
                "flex-col-reverse gap-2": footerConfig.layout === "full-width",
              },
              footerConfig.className,
            )}
          >
            {footerConfig.children ?? footerConfig.startContent}
          </footer>
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
